<!--
 * @Description: 账户管理-流水列表
 * @Date: 2025-05-30 13:56:59
-->
<template>
  <XrkTable
    ref="xrkTableRef"
    :getListAjax="getListAjax"
    :searchConfig="searchConfig"
    :tableConfig="tableConfig"
  >
    <!-- <template #tableBefore>
      <ul class="table__before__summary">
        <li>
          已选择
          <span style="color: #0f62fe">{{ tableBeforeInfo.total }}</span>
          项
        </li>
        <li>
          期初金额：{{ formatter.fen2yuan(tableBeforeInfo.startAmount, '元') }}
        </li>
        <li>
          期末金额：{{ formatter.fen2yuan(tableBeforeInfo.endAmount, '元') }}
        </li>
        <li>
          本期收支金额：{{ formatter.fen2yuan(tableBeforeInfo.amount, '元') }}
        </li>
      </ul>
    </template> -->
  </XrkTable>
</template>

<script lang="tsx" setup>
import { ref, defineExpose } from 'vue';
import {
  XrkTable,
  XrkTableProps,
  useTableColumn,
  useSelectColumn,
  useInputColumn,
  useDatePickerColumn,
  BaseButton,
  XrkMessageBox,
  XrkMessage
} from 'xrk-components';
import { formatter, http } from '@/utils';
import { PAY_PASSAGEWAY_MAPPING, INOUT_TYPE } from '@/common/constants/bank';
import {
  exportTranStatementList,
  downloadVoucher,
  selectTransStatementList
} from '../data-source/apis';
import { SelectTransStatementData } from '../data-source/types';
import appStore from '@/store';
import { storeToRefs } from 'pinia';

const { fyBank } = PAY_PASSAGEWAY_MAPPING;

const { info: userInfo } = storeToRefs(appStore.useUserStoreSetup);

const xrkTableRef = ref();
const tableBeforeInfo = ref<Omit<SelectTransStatementData, 'list'>>({
  startAmount: 0,
  endAmount: 0,
  amount: 0,
  total: 0
});

const getListAjax = async params => {
  const resp = await selectTransStatementList({
    ...params,
    functionCode: userInfo.value.functionCode,
    departmentId: userInfo.value.depId
  });
  tableBeforeInfo.value = resp.data;
  return resp;
};

const searchConfig = ref<XrkTableProps['searchConfig']>({
  searchBtn: {
    show: true,
    text: '搜索'
  },
  resetBtn: {
    show: true,
    text: '重置'
  },
  searchColumns: [
    useInputColumn({
      prop: 'oppName',
      label: '企业名称',
      config: {
        type: 'text',
        placeholder: '请输入'
      }
    }),
    useSelectColumn({
      prop: 'type',
      label: '支付方向',
      config: {
        placeholder: '请选择',
        options: INOUT_TYPE
      }
    }),
    useDatePickerColumn({
      prop: 'time',
      label: '创建时间',
      defaultValue: [
        formatter.date(Date.now(), 'YYYY-MM-DD'),
        formatter.date(Date.now(), 'YYYY-MM-DD')
      ],
      config: {
        type: 'daterange',
        startPlaceholder: '时间始',
        endPlaceholder: '时间止',
        clearable: true
      }
    }),
    useSelectColumn({
      prop: 'payPassagewayCode',
      label: '支付通道',
      config: {
        placeholder: '请选择',
        options: [fyBank]
      }
    })
  ],
  otherComponents: [
    {
      render() {
        return (
          <>
            <BaseButton
              v-resource={[['导出']]}
              type="primary2"
              click={async () => {
                const tableData = xrkTableRef.value.getTableData();
                if (!tableData.value.length) {
                  XrkMessage.warning('没有符合当前搜索条件的数据可以导出');
                  return;
                }
                await exportTranStatementList(
                  xrkTableRef.value.getSearchValue()
                );
              }}
            >
              导出
            </BaseButton>
          </>
        );
      }
    },
    {
      render() {
        return (
          <>
            <BaseButton
              v-resource={[['下载回执单']]}
              type="primary2"
              click={async () => {
                const fileName = `批量下载电子回单_${formatter.date(
                  Date.now(),
                  'YYYY-MM-DD'
                )}`;
                const { value } = await XrkMessageBox.prompt(
                  '自定义文件名',
                  '下载回单',
                  {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    inputValue: fileName
                  }
                );
                await downloadVoucher({
                  zipName: value,
                  ...xrkTableRef.value.getSearchValue()
                });
                XrkMessage.success(
                  '文件已经在下载中心内进行生成，如需下载请前往下载中心'
                );
              }}
            >
              批量下载回执单
            </BaseButton>
          </>
        );
      }
    }
  ]
});

const tableConfig = ref<XrkTableProps['tableConfig']>({
  tableColumns: [
    useTableColumn({
      type: 'index',
      label: '序号',
      width: '80px',
      fixed: 'left'
    }),
    useTableColumn({
      label: '对手账户',
      prop: 'oppName',
      width: 300
    }),
    useTableColumn({
      label: '对手账户银行卡号',
      prop: 'oppAccount',
      width: '180px'
    }),
    useTableColumn({
      label: '金额',
      prop: 'price',
      formatter: ({ cellValue }) => formatter.fen2yuan(cellValue)
    }),
    useTableColumn({
      label: '费用类型',
      prop: 'statementType',
      formatter: ({ row }) =>
        formatter.defaultFormat(row.statementType, () => {
          return INOUT_TYPE[row.type]?.option?.[row.statementType]?.label;
        })
    }),
    useTableColumn({
      label: '收支方向',
      prop: 'type',
      formatter: ({ cellValue }) =>
        formatter.defaultFormat(cellValue, () => INOUT_TYPE[cellValue].label)
    }),
    useTableColumn({
      label: '流水号',
      prop: 'statementNo',
      width: 220
    }),
    useTableColumn({
      label: '创建时间',
      prop: 'createAt',
      width: '180px',
      formatter: ({ cellValue }) => formatter.date(cellValue)
    }),
    useTableColumn({
      label: '支付通道',
      prop: 'payPassagewayName'
    }),
    useTableColumn({
      label: '备注',
      prop: 'remark'
    }),
    useTableColumn({
      label: '操作',
      prop: 'handlerCol',
      fixed: 'right',
      width: '130px',
      render: ({ row }) => {
        const { payVoucherUrl, type } = row;
        return (
          <>
            {payVoucherUrl && (
              <div>
                <BaseButton
                  v-resource={[['下载回执单']]}
                  type="text"
                  click={async () => {
                    await http.download(
                      {
                        url: payVoucherUrl,
                        method: 'GET'
                      },
                      {
                        downloadFileName: `电子回单_${
                          INOUT_TYPE[type].label
                        }_${formatter.date(Date.now(), 'YYYYMMDD')}`
                      }
                    );
                  }}
                >
                  下载回执单
                </BaseButton>
              </div>
            )}
          </>
        );
      }
    })
  ]
});

defineExpose({
  init: () => {
    xrkTableRef.value.initCurrentList();
  }
});
</script>
<style lang="scss" scoped>
.table__before__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #dde1e6;
  box-sizing: border-box;
  padding: 16px 0;

  > li {
    font-weight: 400;
    font-size: 14px;
    color: #121619;
    line-height: 22px;
    text-align: left;
    font-style: normal;
    text-transform: none;
  }
}
</style>
